/**
 * Saved-zone persistence + boot hydration + playback-start zone pick.
 * Resolves maestro_connector_active_zone before the first _playTrack dispatch.
 */

import { useMaestroStore } from '../store/maestroStore';
import { connectorRendererFromZone } from './gaplessSetNext';
import { canRestoreSavedZone } from './playbackSession';
import { isConnectorAvailable } from '../lib/connector';
import { LOCAL_ZONE_STORAGE_ID, isLocalZoneStorageId } from '../utils/localZoneStorage';
import { labelFromSavedMeta } from './zoneRestoreMeta';
import { isConnectorBitPerfectCapable } from '../utils/connectorCapability';
import { useOperationProgressStore } from '../store/operationProgressStore';
import { syncRendererConnectivityFromZones } from './rendererConnectivity';
import { isIOSNativeApp } from '../utils/platform';
import { resolveZoneDisplayName } from '../utils/zoneDisplayName';
import type { ZoneRow } from './zoneUsbDacRemap';
import { findPreferredZone } from './zoneRestorePreferred';
import { hydrateGhostUsbDacZone } from './zoneRestoreGhostHydrate';
import { findLiveZoneForSaved } from './zoneRestoreLiveFind';
export { findPreferredZone } from './zoneRestorePreferred';
export type { ZoneRow } from './zoneUsbDacRemap';
const CONNECTOR_ZONE_STORAGE_KEY = 'maestro_connector_active_zone';
const IOS_ZONE_STORAGE_KEY = 'maestro_ios_active_zone';
export const SAVED_ZONE_META_KEY = 'maestro_saved_zone_meta';
const ZONE_WAIT_MS = 8000;
const ZONE_POLL_MS = 250;

export type SavedZoneMeta = {
  id: string;
  name: string;
  type?: string;
};

function activeZoneStorageKey(): string {
  return isIOSNativeApp() ? IOS_ZONE_STORAGE_KEY : CONNECTOR_ZONE_STORAGE_KEY;
}

export function readSavedZoneId(): string | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    return localStorage.getItem(activeZoneStorageKey());
  } catch {
    return null;
  }
}

export function readSavedZoneMeta(): SavedZoneMeta | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(SAVED_ZONE_META_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SavedZoneMeta;
    if (!parsed?.id || !parsed?.name) return null;
    return parsed;
  } catch {
    return null;
  }
}

/** Persist display metadata alongside the zone id for offline / pre-discovery UI. */
export function persistSavedZoneMeta(zone: ZoneRow): void {
  if (typeof localStorage === 'undefined' || !zone?.id) return;
  try {
    const store = useMaestroStore.getState();
    const displayName = resolveZoneDisplayName(zone, {
      discoveredZones: store.discovery?.discoveredZones,
      selectedUsbDac: store.selectedUsbDac,
    });
    const rawName = String(zone.name || '').trim();
    const name = (!isLocalZone(zone) && /^system (output|audio)$/i.test(String(displayName).trim()))
      ? (rawName && !/^system (output|audio)$/i.test(rawName) ? rawName : displayName)
      : displayName;
    const meta: SavedZoneMeta = { id: zone.id, name, type: zone.type };
    localStorage.setItem(SAVED_ZONE_META_KEY, JSON.stringify(meta));
  } catch { /* quota */ }
}

export function getSavedConnectorZoneId(): string | null {
  if (!isConnectorAvailable() && !isIOSNativeApp()) return null;
  return readSavedZoneId();
}

function isLocalZone(zone: ZoneRow): boolean {
  return zone.type === 'browser' || zone.type === 'local'
    || zone.id === LOCAL_ZONE_STORAGE_ID
    || zone.id === 'this_computer'
    || zone.id === 'this_device';
}

function makeLocalComputerZone(): ZoneRow {
  return {
    id: LOCAL_ZONE_STORAGE_ID,
    name: 'System Audio',
    type: 'browser',
    host: '127.0.0.1',
  };
}

export function buildPinnedZoneFromSaved(savedId: string): ZoneRow {
  const ghostUsb = hydrateGhostUsbDacZone(savedId);
  if (ghostUsb) return ghostUsb;
  const meta = readSavedZoneMeta();
  if (meta?.id === savedId) {
    const type = meta.type === 'local-native' ? 'local-native' : (meta.type || 'upnp');
    return {
      id: meta.id,
      name: labelFromSavedMeta(meta.id, meta.name),
      type,
    };
  }
  return {
    id: savedId,
    name: savedId,
    type: 'upnp',
  };
}

/** Synchronous store update — do not wait for async restoreConnectorZone. */
export function applyZoneSelectionToStore(zone: ZoneRow): void {
  const store = useMaestroStore.getState();
  let displayName = resolveZoneDisplayName(zone, {
    discoveredZones: store.discovery?.discoveredZones,
    selectedUsbDac: store.selectedUsbDac,
  });
  if (!isLocalZone(zone)) {
    displayName = labelFromSavedMeta(zone.id, displayName);
  }
  if (isLocalZone(zone)) {
    store.setActiveConnectorRenderer(null);
    store.setActiveZone(zone as never);
    store.setZoneName(displayName);
  } else {
    const renderer = { ...connectorRendererFromZone(zone), name: displayName };
    store.setActiveZone({ ...zone, name: displayName } as never);
    store.setActiveConnectorRenderer(renderer);
    store.setZoneName(displayName);
  }
  persistSavedZoneMeta(zone);
  syncRendererConnectivityFromZones();
}

/** Pin saved network zone in UI when discovery has not returned it yet. */
export function applyPinnedSavedZone(savedId: string): void {
  if (isLocalZoneStorageId(savedId)) {
    applyZoneSelectionToStore(makeLocalComputerZone());
    return;
  }
  applyZoneSelectionToStore(buildPinnedZoneFromSaved(savedId));
  if (isConnectorAvailable() || isIOSNativeApp()) {
    void syncDiscoveryActiveZone(savedId);
  }
}

/**
 * When discovery returns a canonical zone id for the saved id (alias / re-key),
 * upgrade store + Connector active zone so transport targets the live renderer.
 */
export function reconcileDiscoveredSavedZone(savedId: string): boolean {
  const zones = useMaestroStore.getState().discovery?.discoveredZones || [];
  const preferred = findLiveZoneForSaved(savedId, zones);
  if (!preferred) return false;

  const store = useMaestroStore.getState();
  const activeId = store.activeConnectorRenderer?.id || store.discovery?.activeZone?.id;
  if (activeId === preferred.id) return false;

  applyZoneSelectionToStore(preferred);
  void syncDiscoveryActiveZone(preferred.id);
  return true;
}

export function isAlreadyOnSavedZone(savedId: string): boolean {
  const store = useMaestroStore.getState();
  const activeId = store.discovery?.activeZone?.id || store.activeConnectorRenderer?.id;
  if (!activeId) return false;
  const zones = store.discovery?.discoveredZones || [];
  const preferred = findLiveZoneForSaved(savedId, zones);
  if (preferred) return activeId === preferred.id;
  if (activeId === savedId) return true;
  return false;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function syncDiscoveryActiveZone(zoneId: string): Promise<void> {
  try {
    const { getClientDiscoveryHandler } = await import('../handlers/ClientDiscoveryHandler');
    const handler = getClientDiscoveryHandler();
    if (typeof handler.setActiveZone === 'function') {
      await handler.setActiveZone(zoneId).catch(() => {});
    }
  } catch { /* optional discovery bookkeeping */ }
}

/**
 * Synchronous cold-start hydration from localStorage — runs after clearConnectorState
 * so the playbar shows the last-used zone before discovery completes.
 */
export function hydrateSavedZoneOnBoot(): void {
  if (!canRestoreSavedZone()) return;

  const savedId = readSavedZoneId();
  if (!savedId) return;

  const store = useMaestroStore.getState();
  if (store.activeConnectorRenderer?.id) return;

  if (isLocalZoneStorageId(savedId)) {
    if (!store.discovery?.activeZone) {
      applyZoneSelectionToStore(makeLocalComputerZone());
    }
    return;
  }

  const zones = store.discovery?.discoveredZones || [];
  const discovered = findPreferredZone(savedId, zones);
  if (discovered) {
    if (!isAlreadyOnSavedZone(savedId)) {
      applyZoneSelectionToStore(discovered);
      void syncDiscoveryActiveZone(discovered.id);
    } else {
      reconcileDiscoveredSavedZone(savedId);
    }
    return;
  }

  if (!store.discovery?.activeZone || store.discovery.activeZone.id !== savedId) {
    console.log('[zone-restore] boot hydrate pinned zone:', savedId);
    applyPinnedSavedZone(savedId);
  } else {
    syncRendererConnectivityFromZones();
  }
}

/**
 * Before playback start: pick saved connector zone when present in the renderer
 * list, or wait up to 8s for discovery. Never runs during an active session.
 */
export async function ensurePlaybackZoneBeforeStart(): Promise<void> {
  if (!isConnectorAvailable() && !isIOSNativeApp()) return;
  if (!canRestoreSavedZone()) return;

  const savedId = getSavedConnectorZoneId();
  if (!savedId) return;

  if (isLocalZoneStorageId(savedId)) {
    const zones = useMaestroStore.getState().discovery?.discoveredZones || [];
    const localZone = zones.find((z) => z.id === LOCAL_ZONE_STORAGE_ID) || makeLocalComputerZone();
    if (!isAlreadyOnSavedZone(savedId)) {
      applyZoneSelectionToStore(localZone);
      void syncDiscoveryActiveZone(localZone.id);
    }
    return;
  }

  if (isAlreadyOnSavedZone(savedId)) {
    reconcileDiscoveredSavedZone(savedId);
    const zones = useMaestroStore.getState().discovery?.discoveredZones || [];
    const live = findPreferredZone(savedId, zones);
    if (live && !useMaestroStore.getState().activeConnectorRenderer) {
      applyZoneSelectionToStore(live);
      void syncDiscoveryActiveZone(live.id);
    } else if (live) {
      void syncDiscoveryActiveZone(live.id);
    }
    return;
  }

  const store = useMaestroStore.getState();
  let zones = store.discovery?.discoveredZones || [];
  let savedZone = findPreferredZone(savedId, zones);

  if (savedZone) {
    if (savedZone.type === 'local-native' && !isConnectorBitPerfectCapable()) {
      const localZone = zones.find((z) => z.id === LOCAL_ZONE_STORAGE_ID) || makeLocalComputerZone();
      applyZoneSelectionToStore(localZone);
      void syncDiscoveryActiveZone(localZone.id);
      return;
    }
    console.log(`[zone-restore] synchronous pick: ${savedZone.name} (${savedZone.id})`);
    applyZoneSelectionToStore(savedZone);
    void syncDiscoveryActiveZone(savedZone.id);
    return;
  }

  const opId = useOperationProgressStore.getState().beginOperation('Connecting to your speaker…', {
    progress: 0.1,
  });
  const deadline = Date.now() + ZONE_WAIT_MS;
  const { getClientDiscoveryHandler } = await import('../handlers/ClientDiscoveryHandler');

  try {
    while (Date.now() < deadline) {
      await sleep(ZONE_POLL_MS);
      await getClientDiscoveryHandler().refreshConnectorRenderers().catch(() => {});
      zones = useMaestroStore.getState().discovery?.discoveredZones || [];
      savedZone = findPreferredZone(savedId, zones);
      if (savedZone) {
        if (savedZone.type === 'local-native' && !isConnectorBitPerfectCapable()) {
          break;
        }
        console.log(`[zone-restore] pick after wait: ${savedZone.name} (${savedZone.id})`);
        applyZoneSelectionToStore(savedZone);
        void syncDiscoveryActiveZone(savedZone.id);
        return;
      }
    }

    console.log('[zone-restore] saved zone not discovered — pinning for reconnect UI');
    applyPinnedSavedZone(savedId);
  } finally {
    useOperationProgressStore.getState().endOperation(opId);
  }
}
export { shouldAllowConnectorZoneRestoreRetry, wireConnectorDiscoveryBoot, runBootZoneRestoreWatch, runConnectorBootMount } from './bootZoneRestoreWatch'; export { ensureBootSessionHydratedFromDisk } from './bootSessionHydrate';
